import { useState, useCallback } from 'react';
import { formatValueWithResolution, parseInputValue } from './utils/formatters';
import { roundToReadability } from '@utils/runtime';

const createFieldKey = (field: string, pointId?: string): string => {
  return pointId ? `${pointId}-${field}` : field;
};

const getDisplayValue = (
  value: string | number | null,
  field: string,
  focusedField: string | null,
  localValues: Record<string, string>,
  resolution: number,
  fieldKey?: string
): string => {
  const lookupKey = fieldKey || field;

  if (focusedField === lookupKey && localValues[lookupKey] !== undefined) {
    return localValues[lookupKey];
  }

  if (value === null || value === '') return '';

  const numValue =
    typeof value === 'number' ? value : parseFloat(String(value));
  if (isNaN(numValue)) return '';

  return formatValueWithResolution(numValue, resolution);
};

const cleanAndParseValue = (value: string): number | null => {
  const cleanValue = value.replace(/,/g, '');
  return parseInputValue(cleanValue);
};

export const useInputFieldState = () => {
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [localValues, setLocalValues] = useState<Record<string, string>>({});
  const [originalValues, setOriginalValues] = useState<Record<string, string>>(
    {}
  );

  const handleFocus = useCallback(
    (
      field: string,
      event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const pointId = event.target.getAttribute('data-point-id') || undefined;
      const fieldKey = createFieldKey(field, pointId);

      setFocusedField(fieldKey);
      const rawValue = event.target.value.replace(/,/g, '');
      setLocalValues(prev => ({ ...prev, [fieldKey]: rawValue }));
      setOriginalValues(prev => ({ ...prev, [fieldKey]: rawValue }));
      setTimeout(() => {
        event.target.select();
      }, 0);
    },
    []
  );

  const handleChange = useCallback(
    (field: string, value: string, pointId?: string) => {
      const fieldKey = createFieldKey(field, pointId);
      setLocalValues(prev => ({ ...prev, [fieldKey]: value }));
    },
    []
  );

  const saveFieldValue = useCallback(
    (
      field: string,
      onUpdate: (
        field: string,
        value: number | null,
        options?: { immediate?: boolean }
      ) => void,
      resolution?: number,
      readability?: number,
      pointId?: string
    ) => {
      const fieldKey = createFieldKey(field, pointId);
      const currentValue = localValues[fieldKey] || '';
      const originalValue = originalValues[fieldKey] || '';

      if (currentValue === originalValue) {
        setLocalValues(prev => {
          const newValues = { ...prev };
          delete newValues[fieldKey];
          return newValues;
        });
        setOriginalValues(prev => {
          const newValues = { ...prev };
          delete newValues[fieldKey];
          return newValues;
        });
        return;
      }

      let numValue = cleanAndParseValue(currentValue);

      if (
        numValue !== null &&
        resolution !== undefined &&
        readability !== undefined
      ) {
        numValue = roundToReadability(numValue, readability, resolution);
      } else if (numValue !== null && resolution !== undefined) {
        numValue = Number(numValue.toFixed(resolution));
      }

      onUpdate(field, numValue, { immediate: true });

      setLocalValues(prev => {
        const newValues = { ...prev };
        delete newValues[fieldKey];
        return newValues;
      });
      setOriginalValues(prev => {
        const newValues = { ...prev };
        delete newValues[fieldKey];
        return newValues;
      });
    },
    [localValues, originalValues]
  );

  const handleBlur = useCallback(
    (
      field: string,
      onUpdate: (
        field: string,
        value: number | null,
        options?: { immediate?: boolean }
      ) => void,
      resolution?: number,
      readability?: number,
      pointId?: string
    ) => {
      setFocusedField(null);
      saveFieldValue(field, onUpdate, resolution, readability, pointId);
    },
    [saveFieldValue]
  );

  const handleKeyDown = useCallback(
    (
      field: string,
      e: React.KeyboardEvent,
      onUpdate: (
        field: string,
        value: number | null,
        options?: { immediate?: boolean }
      ) => void,
      resolution?: number,
      readability?: number,
      pointId?: string
    ) => {
      if (e.key === 'Enter') {
        saveFieldValue(field, onUpdate, resolution, readability, pointId);
      }
    },
    [saveFieldValue]
  );

  const getFormattedDisplayValue = useCallback(
    (
      value: string | number | null,
      field: string,
      resolution: number,
      pointId?: string
    ): string => {
      const fieldKey = createFieldKey(field, pointId);
      return getDisplayValue(
        value,
        field,
        focusedField,
        localValues,
        resolution,
        fieldKey
      );
    },
    [focusedField, localValues]
  );

  return {
    focusedField,
    localValues,
    handleFocus,
    handleChange,
    handleBlur,
    handleKeyDown,
    getFormattedDisplayValue
  };
};
